/**
 * edge.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { GraphNode } from "./node";

/**
 * An additional edge that has the same source and target as another; used when
 * a graph allows duplicate edges but only wants the simulator to see one.
 * NOTE: Its fields match the same fields on {@link GraphEdge}.
 */
export interface SecondaryEdge
{
  label : string;
  weight? : number;
  reverseLabel? : string;
  category? : string;
}

/**
 * A {@link GraphEdge} is the formal concept of an edge (or link) between nodes
 * in a {@link Graph}. As with most of the client's graphing API, the ultimate
 * purpose is for exploring and displaying such data.
 */
export class GraphEdge implements SecondaryEdge
{
  /**
   * A value assigned to the edge. Depending on the graph, this could represent
   * the strength of a connection or the capacity of a path, or any number of
   * edge-based parameters.
   */
  public weight? : number;

  /**
   * A human readable string describing the edge. If a reverse label is also
   * applied, it's assumed that this label is associated with the "forward"
   * direction of the edge (source-to-target).
   */
  public label : string;

  /**
   * A human readable description for the inverse of this edge (i.e. for the
   * direction target-to-source)
   */
  public reverseLabel? : string;

  /**
   * A category describing the kind of link.
   */
  public category? : string;

  /**
   * A slot used by D3 to track the edge.
   */
  public index? : number;

  /**
   * The source node for the edge.
   * NOTE: Edges are always directed, but the parent graph may not care about
   * an edge's orientation.
   */
  public source : GraphNode;

  /**
   * The target node for the edge.
   * NOTE: Edges are always directed, but the parent graph may not care about
   * an edge's orientation.
   */
  public target : GraphNode;

  /**
   * Whether the edge should be "on" and displayed in the graph.
   */
  public enabled : boolean = true;

  /**
   * Whether the edge has been processed by the simulation engine (currently D3)
   */
  public simulated : boolean = false;

  /**
   * Create a new edge for a graph structure
   * @param source The source of the edge
   * @param target The target of the edge
   */
  constructor (source : GraphNode, target : GraphNode)
  {
    this.source = source;
    this.target = target;
  }

  /**
   * Whether the edge has additional "secondary" labels. (This provides a cheap
   * method of supporting multi-edges without needing to change the behavior
   * of the simulator.)
   * NOTE: Secondaries aren't supported by GraphEdge as a superclass, so they
   * are set to readonly false. It's expected that subclasses override this.
   */
  public readonly hasSecondaries : boolean = false;

  /**
   * The list of secondary edges held by this edge.
   * NOTE: The ordering of secondaries and their parent may be irrelevant and
   * only due to time; they are only "secondary" from the perspective of the
   * simulator, which only sees the parent as a physics/display object.
   * NOTE: See above inre the property's faux-abstractness via readonly.
   */
  public readonly secondaries : SecondaryEdge[] = [];

  /**
   * Return all of the (forward) labels of this edge and its secondaries.
   */
  public get labelsWithSecondaries () : string[]
  {
    if (this.hasSecondaries)
    {
      return (
        [this.label].concat(
          this.secondaries.map(
            (s : SecondaryEdge) => s.label)));
    }
    else
    {
      return [this.label];
    }
  }

  /**
   * Return all of the reverse labels of this edge and its secondaries.
   */
  public get reverseLabelsWithSecondaries () : string[]
  {
    if (this.hasSecondaries)
    {
      return (
        [this.reverseLabel].concat(
          this.secondaries.map(
            (s : SecondaryEdge) => s.reverseLabel)));
    }
    else
    {
      return [this.reverseLabel];
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/graphs/edge.ts